import React, { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';

import { ModalAcademyPromo } from 'src/components/ModalAcademyPromo';
import { ModalPromo } from 'src/components/ModalPromo';
import { ModalRegistration, ModalRegistrationProps } from 'src/components/ModalRegistration';
import { ModalTTC } from 'src/components/ModalTTC';
import routes from 'src/constants/routes';
import general from 'src/utils/general';
import time from 'src/utils/time';

type ModalContextProps = {
  modalData: ModalData;
  setModalData: (modalData: ModalData) => void;
  close: () => void;
};

type ModalProviderProps = {
  children: ReactNode | ReactNode[];
};

type Modal = 'promo' | 'academyPromo' | 'register' | 'ttc';

type ModalData =
  | {
      type: 'none';
      props: {};
    }
  | {
      type: 'promo';
      props: {};
    }
  | {
      type: 'academyPromo';
      props: {};
    }
  | {
      type: 'register';
      props: ModalRegistrationProps;
    }
  | {
      type: 'ttc';
      props: {};
    };

export const MODALS: Record<Modal, React.FC<any>> = {
  promo: ModalPromo,
  academyPromo: ModalAcademyPromo,
  register: ModalRegistration,
  ttc: ModalTTC,
};

const ModalContext = createContext<ModalContextProps>({
  modalData: {
    type: 'none',
    props: {},
  },
  setModalData: () => {},
  close: () => {},
});

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [modalData, setModalData] = useState<ModalData>({
    type: 'none',
    props: {},
  });

  const getPackageLabel = useCallback(() => {
    const subscriptionKeyParam = window.location.hash.split('-')[1];

    return subscriptionKeyParam ? subscriptionKeyParam.toUpperCase() : undefined;
  }, []);

  const getPromoModalType = useCallback(() => {
    const isPromoDate = time.getIsPromoDate();
    const canHavePromoModal = isPromoDate;

    const isAcademyPromoDate = time.getIsAcademyPromoDate();
    const isAcademyUrl = window.location.pathname === routes.academy;
    const canHaveAcademyPromoModal = isAcademyPromoDate && !isAcademyUrl;

    const isTTCPromoDate = time.getIsTTCPromoDate();
    const isTTCUrl = window.location.pathname === routes.tntProgram;
    const canHaveTTCPromoModal = isTTCPromoDate && !isTTCUrl;

    if (canHaveTTCPromoModal) {
      return 'ttc';
    }

    const canHaveAllPromoModals = canHavePromoModal && canHaveAcademyPromoModal;
    if (canHaveAllPromoModals) {
      return general.getIs50Chance() ? 'promo' : 'academyPromo';
    }

    if (canHavePromoModal) {
      return 'promo';
    }

    if (canHaveAcademyPromoModal) {
      return 'academyPromo';
    }

    return 'none';
  }, []);

  const openPromoModal = useCallback(() => {
    if (getPackageLabel()) {
      return;
    }

    setTimeout(() => {
      setModalData({ type: 'promo', props: {} });
    }, 3000);
  }, [getPackageLabel]);

  const openAcademyPromoModal = useCallback(() => {
    if (getPackageLabel()) {
      return;
    }

    setTimeout(() => {
      setModalData({ type: 'academyPromo', props: {} });
    }, 3000);
  }, [getPackageLabel]);

  const openTTCModal = useCallback(() => {
    if (getPackageLabel()) {
      return;
    }

    setTimeout(() => {
      setModalData({ type: 'ttc', props: {} });
    }, 3000);
  }, [getPackageLabel]);

  const close = useCallback(() => {
    setModalData({
      type: 'none',
      props: {},
    });
  }, []);

  useEffect(() => {
    const promoModalType = getPromoModalType();

    if (promoModalType === 'promo') {
      openPromoModal();
    }

    if (promoModalType === 'academyPromo') {
      openAcademyPromoModal();
    }

    if (promoModalType === 'ttc') {
      openTTCModal();
    }
  }, [getPromoModalType, openPromoModal, openAcademyPromoModal, openTTCModal]);

  return (
    <ModalContext.Provider
      value={{
        modalData,
        setModalData,
        close,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const useModalContext = (): ModalContextProps => {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error('useModalContext must be used within a ModalProvider');
  }

  return context;
};
